/** Used for resources that don't require any particular scope, like domains */
export const NO_REQUIRED_SCOPE = "";

/**
 * ShopifyAuthenticatedReadWriteScopes are unprefixed scope IDs, where `read_<resource>` and `write_<resource>` exist as Shopify access scope IDs/"handlers"
 * https://shopify.dev/docs/api/usage/access-scopes#authenticated-access-scopes
 */
export const ShopifyAuthenticatedReadWriteScopes = [
  "assigned_fulfillment_orders",
  "cart_transforms",
  "checkouts",
  "companies",
  "content",
  "customer_merge",
  "customer_payment_methods",
  "customers",
  "delivery_customizations",
  "discounts",
  "draft_orders",
  "files",
  "fulfillments",
  "gift_cards",
  "inventory",
  "locales",
  "marketing_events",
  "markets",
  "merchant_managed_fulfillment_orders",
  "metaobject_definitions",
  "metaobjects",
  "online_store_navigation",
  "order_edits",
  "orders",
  "own_subscription_contracts",
  "payment_customizations",
  "payment_terms",
  "price_rules",
  "privacy_settings",
  "products",
  "publications",
  "purchase_options",
  "reports",
  "resource_feedbacks",
  "returns",
  "script_tags",
  "shipping",
  "shopify_payments_dispute_evidences",
  "store_credit_account_transactions",
  "taxes",
  "themes",
  "third_party_fulfillment_orders",
  "translations",
  "gift_card_adjustments",
  "payment_mandate",
  "checkout_branding_settings",
  "validations",
  "locations",
  "discounts_allocator_functions",
  "payment_sessions",
  "payment_gateways",
  "inventory_shipments",
  "inventory_transfers",
  "markets_home",
  "product_listings",
  "online_store_pages",
  "customer_data_erasure",
  "gift_card_transactions",
  "product_feeds",
  "gdpr_data_request",
  "inventory_shipments_received_items",
  "pixels",
  "fulfillment_constraint_rules",
  "app_proxy",
  "channels",
  "custom_fulfillment_services",
  "legal_policies",
  "buyer_membership_orders",
  "images",
  "carts",
  "subscription_contracts",
  "users",
  "billing",
];

/**
 * ShopifySimpleScopes are strings that each fully match a Shopify authenticated access scope ID
 * https://shopify.dev/api/admin/access-scopes
 */
export const ShopifyAuthenticatedSimpleScopes = [
  "read_all_orders",
  "read_shopify_payments_disputes",
  "read_shopify_payments_payouts",
  "read_customer_events",
  "read_audit_events",
  "read_merchant_approval_signals",
  "read_store_credit_accounts",
  "read_marketplace_orders",
  "read_marketplace_fulfillment_orders",
  "read_shopify_payments_accounts",
  "read_financial_kyc_information",
  "read_shopify_payments_bank_accounts",
  "read_analytics",
  "read_all_cart_transforms",
  "read_collection_listings",
  "read_cash_tracking",
  "read_apps",
];

/**
 * ShopifyUnauthenticatedAccessScopes are strings that each fully match a Shopify unauthenticated access scope ID
 * https://shopify.dev/docs/api/usage/access-scopes#unauthenticated-access-scopes
 */
export const ShopifyUnauthenticatedSimpleScopes = [
  "unauthenticated_read_customer_tags",
  "unauthenticated_read_content",
  "unauthenticated_read_metaobjects",
  "unauthenticated_read_product_inventory",
  "unauthenticated_read_product_listings",
  "unauthenticated_read_product_pickup_locations",
  "unauthenticated_read_product_tags",
  "unauthenticated_read_selling_plans",
];

export const ShopifyCustomerAccessScopes = [
  "customer_read_customers",
  "customer_write_customers",
  "customer_read_orders",
  "customer_write_orders",
  "customer_read_draft_orders",
  "customer_read_markets",
  "customer_read_metaobjects",
  "customer_read_store_credit_accounts",
  "customer_read_own_subscription_contracts",
  "customer_write_own_subscription_contracts",
  "customer_write_subscription_contracts",
  "customer_read_companies",
  "customer_write_companies",
  "customer_read_locations",
  "customer_write_locations",
];

export const ShopifyUnauthenticatedReadWriteScopes = ["unauthenticated_customers", "unauthenticated_checkouts"];

export const ShopifyReadWriteScopes = [...ShopifyAuthenticatedReadWriteScopes, ...ShopifyUnauthenticatedReadWriteScopes];
export const ShopifySimpleScopes = [...ShopifyAuthenticatedSimpleScopes, ...ShopifyUnauthenticatedSimpleScopes];
/**
 * https://shopify.dev/api/admin/access-scopes
 */
export const AllShopifyScopes = [
  ...ShopifyAuthenticatedReadWriteScopes.flatMap((s) => [`read_${s}`, `write_${s}`]),
  ...ShopifyAuthenticatedSimpleScopes,
  ...ShopifyUnauthenticatedReadWriteScopes.flatMap((s) => {
    const resource = s.replace(/^(unauthenticated_read_|unauthenticated_write_|unauthenticated_)/, "");
    return [`unauthenticated_read_${resource}`, `unauthenticated_write_${resource}`];
  }),
  ...ShopifyUnauthenticatedSimpleScopes,
];

export const CoreShopifyScopes = ["products", "orders", "customers", "checkouts"];

export const namespaceFromScope = (scope: string) =>
  ShopifyAuthenticatedSimpleScopes.includes(scope) ? scope : scope.replace(/^(read|write)_/, "");

export const normalizeScopes = (scopes: string[]) => {
  return Array.from(
    new Set(
      scopes.filter((scope) => {
        if (scope.startsWith("write") || scope.startsWith("unauthenticated_write")) {
          return true;
        }
        // Keep read scopes only if there's no corresponding write scope
        if (scope.startsWith("read_")) {
          const writeScope = `write_${scope.slice(5)}`;
          return !scopes.includes(writeScope);
        }
        if (scope.startsWith("unauthenticated_read_")) {
          const writeScope = `unauthenticated_write_${scope.slice(21)}`;
          return !scopes.includes(writeScope);
        }
        return false;
      })
    )
  ).sort();
};

/**
 * Returns an array of denormalized scopes that include implicit scopes for all scopes in the given list
 * I.e -> ["write_products" ] -> ["write_products", "read_products"] since read is implied from write
 */
export const denormalizeScopes = (scopes: string[]) => {
  const allScopes = scopes.flatMap((scope) => {
    if (scope.startsWith("write")) {
      if (ShopifyAuthenticatedSimpleScopes.includes(scope)) {
        return scope;
      }
      const resource = scope.replace(/^write_/, "");
      if (ShopifyAuthenticatedReadWriteScopes.includes(resource)) {
        return [scope, `read_${resource}`];
      }
    }
    if (scope.startsWith("unauthenticated_write")) {
      if (ShopifyUnauthenticatedSimpleScopes.includes(scope)) {
        return scope;
      }
      const resource = scope.replace(/^unauthenticated_write_/, "");
      return [scope, `unauthenticated_read_${resource}`];
    }
    return scope;
  });
  return Array.from(new Set(allScopes)).sort();
};
